import Air from "@mui/icons-material/Air";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Waves from "@mui/icons-material/Waves";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { alpha, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { encampBrandColors } from "colors";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { GOV_STATUS_LABEL } from "encamp-shared/src/violations/violationLabels";
import {
  HarmonizedViolationGovStatus,
  HarmonizedViolationMatchProvenance,
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { Fragment, ReactNode } from "react";

import { PROGRAM_COLORS } from "./violationProgramColors";

export {
  formatDaysToResolution,
  formatDiscoveryQuarter,
} from "encamp-shared/src/violations/violationLabels";
export { GOV_STATUS_LABEL };

/**
 * A row is stale when EPA stopped reporting it: its lastSeenAt predates the
 * start of the most recent successful refresh. Compared against the refresh
 * START (not finish) — a row touched mid-run has lastSeenAt >= startedAt, so
 * finishedAt would false-flag it. No refresh to compare against → never stale.
 */
export function isViolationStale(
  lastSeenAt: string,
  refreshStartedAt: string | null | undefined
): boolean {
  if (!refreshStartedAt) return false;
  return new Date(lastSeenAt).getTime() < new Date(refreshStartedAt).getTime();
}

export function formatViolationDate(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
}

/** A date we can actually print — anything else has to drop out of a sentence. */
function isUsableDate(value: string | null | undefined): value is string {
  return Boolean(value) && DateTime.fromISO(value ?? "").isValid;
}

/**
 * "<Status> <when>" for the read-only Reported status, so EPA's own resolution
 * status always carries the date it applies to.
 *
 * The event date (`violationEndDate`) is used for RESOLVED only. UNRESOLVED has
 * no determination date that means the same thing across programs — water's
 * violationStartDate is the monitoring period end, not the date EPA determined
 * anything — so dating it would be a wrong date that reads as working software.
 * UNKNOWN has none by definition. A date we can't parse leaves the sentence
 * rather than formatting as an em dash inside it.
 */
export function formatReportedStatus(
  status: HarmonizedViolationResolutionStatus,
  violationEndDate: string | null | undefined,
  lastSeenAt: string | null | undefined
): string {
  const label = prettyPrintEnumValue(status);
  const resolvedOn =
    status === HarmonizedViolationResolutionStatus.Resolved && isUsableDate(violationEndDate)
      ? ` on ${formatViolationDate(violationEndDate)}`
      : "";
  if (!isUsableDate(lastSeenAt)) return `${label}${resolvedOn}`;
  const confirmed = formatViolationDate(lastSeenAt);
  if (resolvedOn) return `${label}${resolvedOn} (confirmed ${confirmed})`;
  return `${label} as of ${confirmed}`;
}

export function formatDiscoveryMonth(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat("LLL yyyy") : "—";
}

export const SEVERITY_COLOR: Record<HarmonizedViolationSeverity, "error" | "warning" | "info"> = {
  [HarmonizedViolationSeverity.High]: "error",
  [HarmonizedViolationSeverity.Medium]: "warning",
  [HarmonizedViolationSeverity.Low]: "info",
};

const STATUS_COLOR: Record<HarmonizedViolationResolutionStatus, "error" | "success" | "default"> = {
  [HarmonizedViolationResolutionStatus.Unresolved]: "error",
  [HarmonizedViolationResolutionStatus.Resolved]: "success",
  [HarmonizedViolationResolutionStatus.Unknown]: "default",
  [HarmonizedViolationResolutionStatus.NotReported]: "default",
};

/**
 * The selectable tracked statuses, in workflow order. Explicit because the
 * generated TS enum is alphabetized — walking the workflow (assessment →
 * resolution → review → resolved) has to be spelled out. The won't-do closure
 * sits last: terminal like Resolved, but an explicit decision not to act rather
 * than an outcome.
 *
 * Feeds both the drawer's status dropdown and the omnisearch `govStatus:`
 * filter popover, so adding or reordering a value moves both surfaces.
 */
export const GOV_STATUS_OPTIONS: readonly HarmonizedViolationGovStatus[] = [
  HarmonizedViolationGovStatus.UnderAssessment,
  HarmonizedViolationGovStatus.ResolutionInProgressCustomerReviewing,
  HarmonizedViolationGovStatus.ResolutionInProgressEncampExecuting,
  HarmonizedViolationGovStatus.AgencyReview,
  HarmonizedViolationGovStatus.Resolved,
  HarmonizedViolationGovStatus.ClosedWontDo,
];

// Chip colors walk the workflow: neutral while assessing, active (info) while
// a resolution is in motion, warning while the agency holds the ball, success
// when done. "default" renders as a plain outlined chip like the EPA column's
// neutral statuses — also used for the won't-do closure, which is terminal
// without being an outcome worth colouring as success.
const GOV_STATUS_COLOR: Record<
  HarmonizedViolationGovStatus,
  "info" | "success" | "warning" | "default"
> = {
  [HarmonizedViolationGovStatus.UnderAssessment]: "default",
  [HarmonizedViolationGovStatus.ResolutionInProgressCustomerReviewing]: "info",
  [HarmonizedViolationGovStatus.ResolutionInProgressEncampExecuting]: "info",
  [HarmonizedViolationGovStatus.AgencyReview]: "warning",
  [HarmonizedViolationGovStatus.Resolved]: "success",
  [HarmonizedViolationGovStatus.ClosedWontDo]: "default",
};

/**
 * Chip for the user-tracked "Resolution status" (govStatus). Separate from
 * StatusChip, which renders EPA's reported statuses — the two columns share a
 * visual language but not an enum. No MUI tooltip: this field is whatever the
 * user last set, and EPA-style dated prose here would read as EPA's. A native
 * `title` does carry the full label, because the two "Resolution In Progress
 * (…)" labels differ only in a parenthetical that narrow grid cells ellipsize
 * away.
 */
export function TrackedStatusChip({ status }: { status: HarmonizedViolationGovStatus }) {
  const color = GOV_STATUS_COLOR[status];
  const label = GOV_STATUS_LABEL[status];
  if (color === "default") {
    return <Chip size="small" variant="outlined" label={label} title={label} />;
  }
  return <Chip size="small" label={label} title={label} sx={lightFilledChipSx(color)} />;
}

export const NOT_REPORTED_EXPLANATION =
  "EPA does not report resolution outcomes for this violation type, so its status cannot be confirmed from EPA data.";

const OLD_SIGNAL_YEARS = 5;

export const OLD_SIGNAL_EXPLANATION =
  "This severity is based on a permit that has been expired or terminated for more than five years. If no action is required, set the Resolution status to Resolved.";

/**
 * True when a permit's terminal date (expiration/termination/retirement) is
 * old enough that the severity it drove is likely a stale signal rather than
 * something actionable today. `now` is a parameter (not `new Date()` inline)
 * so callers/tests stay deterministic.
 */
export function isOldPermitSignal(
  permitTerminalDate: string | null | undefined,
  now: Date
): boolean {
  if (permitTerminalDate == null) {
    return false;
  }
  const cutoff = new Date(now);
  cutoff.setFullYear(cutoff.getFullYear() - OLD_SIGNAL_YEARS);
  return new Date(permitTerminalDate) < cutoff;
}

// Each program gets a leading icon (decorative — the text label stays for
// accessibility), so the program reads at a glance without adding another
// fully-colored chip next to severity/status. All @mui/icons-material icons
// share one component type, so `typeof Waves` types the whole set (and avoids a
// named barrel import that the use-explicit-icon-import ast-grep rule forbids).
const PROGRAM_DISPLAY: Record<HarmonizedViolationProgram, { Icon: typeof Waves }> = {
  [HarmonizedViolationProgram.Water]: { Icon: Waves },
  [HarmonizedViolationProgram.Air]: { Icon: Air },
  [HarmonizedViolationProgram.Waste]: { Icon: DeleteOutline },
};

/** The program chips' icon color, for chart series that need a concrete value. */
export function programChartColor(program: HarmonizedViolationProgram): string {
  return PROGRAM_COLORS[program];
}

/** The program chips' icon component, for legends that mark programs the same way. */
export function programIcon(program: HarmonizedViolationProgram): typeof Waves {
  return PROGRAM_DISPLAY[program].Icon;
}

/**
 * Per-program hover copy, naming only the act the chip actually represents.
 * Hovering "Water" should say Clean Water Act — not recite every program.
 */
export const PROGRAM_TOOLTIP: Record<HarmonizedViolationProgram, string> = {
  [HarmonizedViolationProgram.Water]: "Clean Water Act — NPDES discharge violations.",
  [HarmonizedViolationProgram.Air]: "Clean Air Act violations.",
  [HarmonizedViolationProgram.Waste]:
    "Resource Conservation and Recovery Act (RCRA) — hazardous-waste violations.",
};

export function ProgramChip({ program }: { program: HarmonizedViolationProgram }) {
  const { Icon } = PROGRAM_DISPLAY[program];
  const iconColor = PROGRAM_COLORS[program];
  return (
    <Tooltip describeChild title={PROGRAM_TOOLTIP[program]}>
      <Chip
        size="small"
        variant="outlined"
        icon={<Icon />}
        label={prettyPrintEnumValue(program)}
        // Nudge the icon right of the chip's left border so the whitespace on
        // either side of the icon reads symmetrically (default small-chip icon
        // marginLeft is a tight 4px).
        sx={{ "& .MuiChip-icon": { color: iconColor, marginLeft: "6px" } }}
      />
    </Tooltip>
  );
}

/**
 * Per-value display labels for match provenance. A Record so widening the
 * enum is a compile error here rather than silent fall-through in a chip or
 * filter — the same drift protection PROGRAM_DISPLAY/SEVERITY_TOOLTIP use.
 * Copy provisional pending product review.
 */
export const MATCH_PROVENANCE_LABEL: Record<HarmonizedViolationMatchProvenance, string> = {
  [HarmonizedViolationMatchProvenance.Direct]: "Current owner",
  [HarmonizedViolationMatchProvenance.SharedFrs]: "Former owner",
};

/** Narrow an omnisearch/filter string to the provenance enum, no casting. */
export function isMatchProvenance(value: string): value is HarmonizedViolationMatchProvenance {
  return Object.values(HarmonizedViolationMatchProvenance).some((member) => member === value);
}

/**
 * Explains the SHARED_FRS provenance in customer terms. The chip says "Former
 * owner"; this carries the jargon — EPA's one-site-ID filing is why another
 * operator's violation appears here at all. Copy provisional pending product
 * review.
 */
export const PRIOR_OWNER_TOOLTIP =
  "EPA files everything that ever happened at a physical site under one site ID, " +
  "so this violation belongs to a former owner or another operator of this address — " +
  "not to this facility's own permits. Hidden from the default list; " +
  "use the Responsible Party filter to show or hide these.";

/**
 * Tags a violation matched only through the shared-FRS (former owner) path.
 * Outlined `secondary` — customers see it, so it doesn't take the staff hue.
 */
export function PriorOwnerChip() {
  return (
    <Tooltip describeChild title={PRIOR_OWNER_TOOLTIP}>
      <Chip
        size="small"
        variant="outlined"
        color="secondary"
        label={MATCH_PROVENANCE_LABEL[HarmonizedViolationMatchProvenance.SharedFrs]}
      />
    </Tooltip>
  );
}

export const HELD_MATCH_TOOLTIP =
  "Matched to this facility only through an expired or shared alternate ID, so it is " +
  "held back from the customer until staff confirm the match. Open the row to accept or reject it.";

export const REJECTED_MATCH_TOOLTIP =
  "Staff rejected this match as not this facility's violations, so it stays hidden from " +
  "the customer. Open the row to un-reject it.";

/** STAFF-ONLY marker for a held row still pending a decision — the held state must
 *  never surface to a customer. */
export function HeldMatchChip() {
  return (
    <Tooltip describeChild title={HELD_MATCH_TOOLTIP}>
      <Chip size="small" label="Held pending confirmation" sx={lightFilledChipSx("staff")} />
    </Tooltip>
  );
}

/** STAFF-ONLY marker for a rejected row. Muted: a resolved state, not an action. */
export function RejectedMatchChip() {
  return (
    <Tooltip describeChild title={REJECTED_MATCH_TOOLTIP}>
      <Chip
        size="small"
        label="Rejected"
        sx={{ color: "text.secondary", bgcolor: (t: Theme) => t.palette.action.selected }}
      />
    </Tooltip>
  );
}

/**
 * The row's held-match group when it is pending a staff decision, else null.
 * Returning the narrowed group saves callers a second null-check, and is the single
 * definition shared by the grid chip and the drawer control.
 */
export function pendingMatchReview<
  T extends { acceptedAt?: string | null; rejectedAt?: string | null },
>(heldMatch: T | null | undefined, isStaff: boolean): T | null {
  if (
    isStaff &&
    heldMatch != null &&
    heldMatch.acceptedAt == null &&
    heldMatch.rejectedAt == null
  ) {
    return heldMatch;
  }
  return null;
}

/** Boolean form of {@link pendingMatchReview} for chip/height call sites. */
export function showsPendingMatchReview(
  heldMatch: { acceptedAt?: string | null; rejectedAt?: string | null } | null | undefined,
  isStaff: boolean
): boolean {
  return pendingMatchReview(heldMatch, isStaff) != null;
}

/**
 * The row's held-match group when a staff member has rejected it, else null. The
 * single definition shared by the grid's "Rejected" chip and the un-reject panel.
 */
export function rejectedMatchReview<
  T extends { acceptedAt?: string | null; rejectedAt?: string | null },
>(heldMatch: T | null | undefined, isStaff: boolean): T | null {
  // Accepted wins: a stale both-set row is visible (released), so it must never be
  // labelled "rejected / hidden".
  if (
    isStaff &&
    heldMatch != null &&
    heldMatch.acceptedAt == null &&
    heldMatch.rejectedAt != null
  ) {
    return heldMatch;
  }
  return null;
}

/** Boolean form of {@link rejectedMatchReview} for chip/height call sites. */
export function showsRejectedMatch(
  heldMatch: { acceptedAt?: string | null; rejectedAt?: string | null } | null | undefined,
  isStaff: boolean
): boolean {
  return rejectedMatchReview(heldMatch, isStaff) != null;
}

// Light-filled chips use the role's color on an 8%-alpha fill; staff markers use
// mode-invariant Pumpkin. See .cursor/rules/design-system.mdc §Chips.
export function lightFilledChipSx(
  color: "info" | "success" | "error" | "warning" | "secondary" | "staff"
) {
  if (color === "staff") {
    return {
      color: encampBrandColors.pumpkin,
      bgcolor: alpha(encampBrandColors.pumpkin, 0.08),
    };
  }
  return {
    color: `${color}.main`,
    bgcolor: (t: Theme) => alpha(t.palette[color].main, 0.08),
  };
}

/**
 * Per-level hover copy, explaining only the level the chip actually carries.
 * Hovering "High" should say what high means, not list every level.
 *
 * Severity is Encamp's own work-priority rating derived from EPA data — EPA has
 * no cross-program severity field — so each line says what Encamp keys on.
 * Keep in sync with SEVERITY_EXPLANATION below.
 */
export const SEVERITY_TOOLTIP: Record<HarmonizedViolationSeverity, string> = {
  [HarmonizedViolationSeverity.High]:
    "Encamp's highest priority. The facility is under an active EPA or state enforcement order, or EPA's data marks the violation as serious: any air violation, a waste handler EPA calls a significant non-complier, a water discharge in Reportable Non-Compliance, or a violation still open against an expired or terminated water permit.",
  [HarmonizedViolationSeverity.Medium]:
    "Encamp's middle priority. The hazardous-waste handler has unresolved violations, but EPA has not flagged it as a significant non-complier. Only waste violations are rated Medium.",
  [HarmonizedViolationSeverity.Low]:
    "Encamp's lowest priority. None of the High or Medium conditions apply — for example a waste handler with no unresolved violations, or a water violation with no Reportable Non-Compliance flag whose permit is still in force.",
};

export function SeverityChip({ severity }: { severity: HarmonizedViolationSeverity }) {
  return (
    <Tooltip describeChild title={SEVERITY_TOOLTIP[severity]}>
      <Chip
        size="small"
        label={prettyPrintEnumValue(severity)}
        sx={lightFilledChipSx(SEVERITY_COLOR[severity])}
      />
    </Tooltip>
  );
}

export function StatusChip({
  status,
  tooltip,
}: {
  status: HarmonizedViolationResolutionStatus;
  /**
   * Optional hover explanation. Only the grid's Reported status column passes
   * one; the user-tracked Resolution status column renders the bare chip.
   */
  tooltip?: string;
}) {
  const color = STATUS_COLOR[status];
  const chip =
    color === "default" ? (
      // Unknown/Not Reported map to the spec's neutral grey: a plain outlined
      // chip (no color).
      <Chip size="small" variant="outlined" label={prettyPrintEnumValue(status)} />
    ) : (
      <Chip size="small" label={prettyPrintEnumValue(status)} sx={lightFilledChipSx(color)} />
    );

  // Not Reported always explains itself — it states a gap in EPA's feed that a
  // dated caller tooltip can't convey — so it outranks a passed-in tooltip.
  if (status === HarmonizedViolationResolutionStatus.NotReported) {
    return (
      <Tooltip describeChild title={NOT_REPORTED_EXPLANATION} arrow>
        {chip}
      </Tooltip>
    );
  }
  if (!tooltip) return chip;
  return (
    <Tooltip describeChild title={tooltip}>
      {chip}
    </Tooltip>
  );
}

// --- Metric explanations -------------------------------------------------
//
// Severity/Program/Stale are easy to misread (e.g. "why is everything High and
// Water, and why is Medium empty?"). These explanations are surfaced two ways:
// as a rich hover tooltip on the overview headings/cards (via MetricInfoIcon)
// and as the short plain-text header hint on the matching grid column. Keep the
// wording here so both surfaces stay in sync.

type MetricExplanation = {
  /** Lead sentence — the one-line takeaway. */
  summary: string;
  /** Optional per-value breakdown rendered as a bulleted list. */
  bullets?: { term: string; desc: string }[];
  /** Optional closing caveat (rendered italic). */
  caveat?: string;
};

export const SEVERITY_EXPLANATION: MetricExplanation = {
  summary:
    "Severity is Encamp's work-priority rating, derived from EPA data — EPA publishes no single severity field across its three programs, so we compute one.",
  bullets: [
    {
      term: "High",
      desc: "The facility is under an active formal EPA order under the matching statute, or a state has issued a formal enforcement order on the violation; any air violation (EPA reports these only as high-priority or federally reportable); a hazardous-waste (RCRA) handler EPA flags as a significant non-complier; a water (NPDES) discharge EPA flags as Reportable Non-Compliance; or a water violation still open against an expired, terminated, or retired permit.",
    },
    {
      term: "Medium",
      desc: "A hazardous-waste (RCRA) handler with unresolved violations that EPA has not flagged as a significant non-complier. Waste is the only program that produces Medium.",
    },
    {
      term: "Low",
      desc: "Nothing above applies — e.g. a handler with no unresolved RCRA violations, or a water violation with no Reportable Non-Compliance flag whose permit is still in force.",
    },
  ],
  caveat:
    "An order whose settlement has been entered no longer raises severity — it stays on the facility's permanent record, but it is no longer an open matter. So a violation can show an order in ECHO and still not be High. Severity ranks work, it does not measure harm.",
};

export const PROGRAM_EXPLANATION: MetricExplanation = {
  summary: "Violations grouped by EPA media program.",
  bullets: [
    { term: "Water", desc: "NPDES (Clean Water Act) discharge violations." },
    { term: "Air", desc: "Clean Air Act violations." },
    { term: "Waste", desc: "RCRA hazardous-waste violations." },
  ],
  caveat: "Waste appears only after RCRA data has been imported, so it may be empty until then.",
};

const EPA_PULL_CAVEAT =
  "EPA refreshes its published data in periodic pulls rather than continuously, so a violation can appear in a past quarter long after that quarter closed. Recent quarters often fill in over time and may look artificially low at first.";

export const QUARTER_EXPLANATION: MetricExplanation = {
  summary: "Violations bucketed by the calendar quarter in which they began.",
  caveat: EPA_PULL_CAVEAT,
};

export const PROGRAM_TIME_EXPLANATION: MetricExplanation = {
  summary:
    "One band per EPA media program, stacked to the quarterly total, counting all violations by the calendar quarter in which they began.",
  caveat: EPA_PULL_CAVEAT,
};

export const TOP_FACILITIES_EXPLANATION: MetricExplanation = {
  summary: "Up to ten facilities with the most violations matching the current filters.",
};

export const REGION_CHART_EXPLANATION: MetricExplanation = {
  summary: "Unresolved violations grouped by the facility's Region.",
  caveat: "Facilities without a Region are grouped under “(No region)”.",
};

export const BUSINESS_UNIT_CHART_EXPLANATION: MetricExplanation = {
  summary:
    "Unresolved violations grouped by the facility's business unit — the facility's business unit field, or its single facility tag when the field is blank.",
  caveat: "Facilities without a business unit are grouped under “(No business unit)”.",
};

export const PROGRAM_SEVERITY_EXPLANATION: MetricExplanation = {
  summary:
    "Violations grouped by EPA media program, with each bar stacked by Encamp's severity rating.",
};

const MATRIX_PROGRAM_ORDER = [
  HarmonizedViolationProgram.Water,
  HarmonizedViolationProgram.Waste,
  HarmonizedViolationProgram.Air,
] as const;
const MATRIX_SEVERITY_ORDER = [
  HarmonizedViolationSeverity.High,
  HarmonizedViolationSeverity.Medium,
  HarmonizedViolationSeverity.Low,
] as const;

/** The 3×3 program × severity tooltip matrix (program columns, severity rows); missing cells render as 0. */
export function ProgramSeverityMatrix({
  cells,
}: {
  cells: {
    program: HarmonizedViolationProgram;
    severity: HarmonizedViolationSeverity;
    count: number;
  }[];
}) {
  const countFor = (program: HarmonizedViolationProgram, severity: HarmonizedViolationSeverity) =>
    cells.find((cell) => cell.program === program && cell.severity === severity)?.count ?? 0;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto repeat(3, auto)",
        columnGap: 1.5,
        rowGap: 0.25,
        ml: 3,
        mt: 0.5,
      }}
    >
      <Box />
      {MATRIX_PROGRAM_ORDER.map((program) => (
        <Typography key={program} variant="caption" color="text.secondary" fontWeight={600}>
          {prettyPrintEnumValue(program)}
        </Typography>
      ))}
      {MATRIX_SEVERITY_ORDER.map((severity) => (
        <Fragment key={severity}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {prettyPrintEnumValue(severity)}
          </Typography>
          {MATRIX_PROGRAM_ORDER.map((program) => (
            <Typography key={program} variant="caption" align="right">
              {countFor(program, severity)}
            </Typography>
          ))}
        </Fragment>
      ))}
    </Box>
  );
}

export const ASSIGNEE_CHART_EXPLANATION: MetricExplanation = {
  summary:
    "Unresolved violations grouped by their effective assignee — the violation's own assignee, or the facility's default assignee when the violation has none — with each bar stacked by severity.",
  caveat: "Shows up to ten assignees; open violations that nobody owns appear under “Unassigned”.",
};

export const STALE_EXPLANATION: MetricExplanation = {
  summary: "“Stale” means our latest EPA data refresh no longer includes this violation.",
  caveat:
    "The last refresh that still found it is older than our latest one, so it has dropped off EPA's published data — often because it was resolved or superseded upstream, though Encamp hasn't independently confirmed that.",
};

/**
 * Flat-string form of STALE_EXPLANATION for the chip's own hover tooltip. The
 * chip is the hover target (no separate info icon), and a plain Tooltip takes a
 * string, so the structured explanation is flattened rather than rendered.
 */
export const STALE_CHIP_TOOLTIP =
  `${STALE_EXPLANATION.summary} ${STALE_EXPLANATION.caveat ?? ""}`.trim();

/**
 * "Stale" marker for a violation EPA has stopped reporting. Shared by the grid's
 * Last seen cell and the drawer's chip row so the two can't drift.
 */
export function StaleChip() {
  return (
    <Tooltip describeChild title={STALE_CHIP_TOOLTIP}>
      <Chip size="small" color="warning" variant="outlined" label="Stale" />
    </Tooltip>
  );
}

/** Short single-sentence hints for the grid column-header tooltips. */
export const SEVERITY_HEADER_HINT =
  "Encamp's work-priority rating, derived from EPA data (not a harm score): High = an active EPA or state enforcement order, any air violation, an RCRA significant non-complier, water Reportable Non-Compliance, or a violation open against a dead water permit; Medium = an RCRA handler with unresolved violations; Low = everything else.";
export const PROGRAM_HEADER_HINT = "EPA media program.";
// --- Reported status ------------------------------------------------------
//
// Plain strings rather than a MetricExplanation: the drawer surfaces this
// through InfoIconTooltip -> BigTooltip, which renders `\n\n`-delimited text
// under `white-space: pre-wrap` (no bullets, click-to-toggle), matching the
// waste GeneratorStatusTile. Composed with `+` rather than line continuations so
// no indentation leaks into the visible paragraphs.
const REPORTED_STATUS_LAG =
  "EPA data is refreshed weekly and can lag several weeks behind the agency's own records.";

export const REPORTED_STATUS_TOOLTIP =
  "Resolution status exactly as the source government data reports it. Read-only — " +
  "track your own position with the Resolution status field above." +
  "\n\nFor resolved violations we show EPA's own resolution date, then the date we last " +
  "confirmed the status was still current. Otherwise we show only the confirmation date, " +
  "because EPA publishes no reliable determination date across all three programs." +
  "\n\n" +
  REPORTED_STATUS_LAG;

/**
 * One-line version of the above for the grid's Reported status column header.
 * The per-row chip tooltip is the `formatReportedStatus` sentence, not this —
 * this explains what the column is, that one says what the row says.
 */
export const REPORTED_STATUS_HEADER_HINT =
  "Resolution status as reported in the source government data (EPA/state). Read-only — " +
  "dated with EPA's resolution date when resolved, otherwise with the date we last confirmed " +
  "the status. Track your own resolution in the 'Resolution status' column.";

/**
 * Hover copy for the page's top-right "Violations data updated …" label. The
 * label states when OUR refresh last completed; this says what that does and
 * does not tell you about EPA's own records.
 */
export const DATA_FRESHNESS_TOOLTIP =
  "When Encamp last completed a refresh of EPA violation data. " + REPORTED_STATUS_LAG;

export const STALE_HEADER_HINT =
  "Last time one of our EPA data refreshes still found this violation — our own date, not EPA's. “Stale” means it predates our latest refresh, so EPA no longer lists it.";

function ExplanationBody({ explanation }: { explanation: MetricExplanation }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" component="p">
        {explanation.summary}
      </Typography>
      {explanation.bullets?.length ? (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {explanation.bullets.map((b) => (
            <Typography key={b.term} variant="caption" component="li">
              <strong>{b.term}</strong> — {b.desc}
            </Typography>
          ))}
        </Box>
      ) : null}
      {explanation.caveat ? (
        <Typography variant="caption" component="p" sx={{ mt: 0.5, fontStyle: "italic" }}>
          {explanation.caveat}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * A small info icon that reveals a metric explanation on CLICK, in an opaque
 * card. Delegates to the shared InfoIconTooltip (-> BigTooltip), which is the
 * established pattern on the waste facility page — these explanations are long
 * enough that a hover tooltip vanished while being read.
 *
 * `label` names the metric for screen readers (e.g. "severity").
 */
export function MetricInfoIcon({
  explanation,
  label,
}: {
  explanation: MetricExplanation;
  label: string;
}) {
  return (
    <InfoIconTooltip
      tooltip={<ExplanationBody explanation={explanation} />}
      ariaLabel={`About ${label}`}
      iconButtonSx={{ p: 0.25 }}
    />
  );
}

export function SectionHeading({
  children,
  inRow = false,
}: {
  children: ReactNode;
  /** In a row with an icon, which carries the top margin so the two center together. */
  inRow?: boolean;
}) {
  return (
    <Typography variant="overline" color="text.primary" sx={{ mt: inRow ? 0 : 1, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}
